import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X, Check, CheckCheck, FileText, MessageSquare, Receipt, AlertTriangle, Clock } from 'lucide-react';
import useAuthStore from '../../context/authStore';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, getAllUnreadCount, getAllNotifications, markAsReadAsAdmin } from '../../services/notifications';
import { publishNotificationState } from '../../hooks/useNavigationNotifications';

export default function NotificationPanel() {
  const { token, isInternalTeam } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewScope, setViewScope] = useState('mine'); // 'mine' or 'all' (admins only)
  const panelRef = useRef(null);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      setAdminUnreadCount(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const data = await getUnreadCount(token);
        const count = data?.count ?? 0;
        setUnreadCount(Math.max(0, count));
      } catch (err) {
        console.error('Failed to fetch notification count:', err);
        setUnreadCount(0);
      }

      // If internal team, fetch admin-wide unread count too
      try {
        if (isInternalTeam()) {
          const d2 = await getAllUnreadCount(token);
          setAdminUnreadCount(d2?.count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch admin-wide notification count:', err);
        setAdminUnreadCount(0);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [token]);

  // Fetch notifications when panel opens or when view scope changes
  useEffect(() => {
    if (isOpen && token) {
      if (viewScope === 'mine') {
        fetchNotifications();
      } else if (viewScope === 'all' && isInternalTeam()) {
        fetchAdminNotifications();
      }
    }
  }, [isOpen, token, viewScope]);

  const fetchAdminNotifications = async () => {
    setLoading(true);
    try {
      const data = await getAllNotifications(token, { limit: 50 });
      setNotifications(data.notifications || []);
      setAdminUnreadCount(data?.unreadCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch admin notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications(token);
      setNotifications(data.notifications || []);
      publishNotificationState({ kind: 'snapshot', notifications: data.notifications || [] });
      const count = data?.unreadCount ?? data?.count ?? 0;
      setUnreadCount(Math.max(0, count));
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId, token);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, status: 'read' } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      publishNotificationState({ kind: 'read', id: notificationId });
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead(token);
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
      publishNotificationState({ kind: 'read-all' });
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getIcon = (type) => {
    if (type?.includes('document')) return <FileText size={16} className="text-blue-500" />;
    if (type?.includes('message')) return <MessageSquare size={16} className="text-green-500" />;
    if (type?.includes('invoice')) return <Receipt size={16} className="text-purple-500" />;
    if (type?.includes('escalation') || type?.includes('overdue')) return <AlertTriangle size={16} className="text-red-500" />;
    if (type?.includes('signature')) return <Clock size={16} className="text-orange-500" />;
    return <Bell size={16} className="text-gray-500" />;
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const resolveActionUrl = (notification) => {
    const raw = String(notification?.actionUrl || '').trim();
    if (!raw) return '';
    if (raw === '/billing/recurring') {
      return '/invoices';
    }
    return raw;
  };

  return (
    <div className={`relative ${isOpen ? 'z-[90]' : ''}`} ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Notifications"
      >
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto mt-2 sm:w-96 bg-white rounded-xl shadow-lg border z-[100] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {isInternalTeam() && (
                <div className="ml-2 inline-flex items-center gap-1 bg-gray-100 rounded overflow-hidden text-xs">
                  <button onClick={() => setViewScope('mine')} className={`px-2 py-1 ${viewScope === 'mine' ? 'bg-white font-semibold' : 'text-gray-500'}`}>Mine</button>
                  <button onClick={() => setViewScope('all')} className={`px-2 py-1 ${viewScope === 'all' ? 'bg-white font-semibold' : 'text-gray-500'}`}>All</button>
                  {viewScope === 'all' && adminUnreadCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-rose-500 text-white text-xs font-medium">{adminUnreadCount}</span>}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                    notification.status === 'unread' ? 'bg-green-50/50' : ''
                  }`}

                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <p className={`text-sm ${notification.status === 'unread' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        {notification.status === 'unread' && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-green-500 mt-1.5" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-400">
                          {formatTime(notification.createdAt)}
                        </span>

                        <div className="flex items-center gap-2">
                          {resolveActionUrl(notification) && (
                            <Link to={resolveActionUrl(notification)} onClick={() => setIsOpen(false)} className="text-xs text-green-600 hover:text-green-700 font-medium">View -&gt;</Link>
                          )}

                          {notification.status === 'unread' ? (
                            isInternalTeam() && viewScope === 'all' ? (
                              <button
                                onClick={async () => {
                                  try {
                                    await markAsReadAsAdmin(notification._id, token);
                                    // refresh admin list & counts
                                    const d = await getAllNotifications(token, { limit: 50 });
                                    setNotifications(d.notifications || []);
                                    const c = await getAllUnreadCount(token);
                                    setAdminUnreadCount(c?.count || 0);
                                    const mine = await getUnreadCount(token);
                                    setUnreadCount(mine?.count || 0);
                                  } catch (err) {
                                    console.error('Failed to mark as read (admin):', err);
                                  }
                                }}
                                className="text-xs text-green-600 hover:text-green-700"
                              >
                                Mark read
                              </button>
                            ) : (
                              <button onClick={() => handleMarkAsRead(notification._id)} className="text-xs text-green-600 hover:text-green-700">Mark read</button>
                            )
                          ) : (
                            <button onClick={() => { /* dismiss placeholder */ }} className="text-xs text-gray-400">Dismiss</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer intentionally omitted: no dedicated /notifications route in client portal */}
        </div>
      )}
    </div>
  );
}
